import React, { useState, useEffect } from 'react';
import { Grid, Typography, RadioGroup, FormControlLabel, Radio, Button, TextField, Container, CircularProgress } from '@mui/material';
import { Alert } from '@mui/material';
import { makeStyles } from '@mui/styles';
import pesquisaService from 'services/pesquisaService'; 
import FormularioPesquisa from './FormularioPesquisa'; 
import Pergunta from './Pergunta';

const useStyles = makeStyles((theme) => ({
	container: {
		padding: '40px',
		maxWidth: '800px',
		margin: '3em auto',
		backgroundColor: '#fff',
		borderRadius: '4px',
		boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
		fontFamily: '"Times New Roman", Times, serif',
		border: '1px solid #ccc',
		lineHeight: '1.6',
	},
	pergunta: {
		marginBottom: '30px',
	},
	respostaInput: {
		padding: '10px',
		borderRadius: '4px',
	},
	titulo: {
		fontSize: '2.5rem',
		fontWeight: 'bold',
		marginBottom: '2rem',
		color: '#333',
		textAlign: 'center',
		fontFamily: '"Georgia", serif',
		textTransform: 'uppercase',
	},
	perguntaTexto: {
		fontSize: '1.25rem',
		fontWeight: '600',
		marginBottom: '10px',
		color: '#333',
	},
	respostaTexto: {
		fontSize: '1rem',
		color: '#666',
		marginTop: '10px',
	},
	botao: {
		marginTop: '30px',
		padding: '15px 30px',
		fontSize: '1.1rem',
		backgroundColor: 'var(--azul-escuro)',
		color: '#fff',
		'&:hover': {
			backgroundColor: 'var(--azul-escuro)',
		},
		borderRadius: '4px',
	},
	alert: {
		marginBottom: '20px',
	},
	radioGroup: {
		marginTop: '10px',
	},
	loadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		height: '100vh',
	},
	textDocument: {
		fontFamily: '"Times New Roman", Times, serif',
		fontSize: '1.1rem',
		color: '#444',
		margin: '10px 0',
	},
}));

const ResponderQuestionario = () => {
	const classes = useStyles();
	const [perguntas, setPerguntas] = useState([]);
	const [respostas, setRespostas] = useState({});
	const [envioSucesso, setEnvioSucesso] = useState(null);
	const [loading, setLoading] = useState(false);
	const [carregandoPerguntas, setCarregandoPerguntas] = useState(true);
	const [jaRespondido, setJaRespondido] = useState(false);
	const [modoEdicao, setModoEdicao] = useState(true);

	useEffect(() => {
			const verificarEnvioAnterior = async () => {
					try {
							const data = await pesquisaService.verificarEnvioAnterior();
							if (data.jaRespondido) {
									const respostasFormatadas = data.respostas.reduce((acc, resposta) => {
											acc[resposta.id_pergunta] = resposta.resposta;
											return acc;
									}, {});
									setRespostas(respostasFormatadas);
									setJaRespondido(true);
							}
					} catch (error) {
							console.error('Erro ao verificar resposta anterior:', error);
					}
			};

			verificarEnvioAnterior();
	}, []);

	useEffect(() => {
			const fetchDados = async () => {
					try {
							const perguntasData = await pesquisaService.fetchPerguntas();
							setPerguntas(perguntasData);
					} catch (error) {
							console.error('Erro ao carregar dados:', error);
					} finally {
							setCarregandoPerguntas(false);
					}
			};

			fetchDados();
	}, []);

	const handleChange = (event, perguntaId) => {
    // Se a pergunta for checkbox (múltipla escolha), tratamos como array
    if (event.target.type === 'checkbox') {
        const value = event.target.value;
        const checked = event.target.checked;

        // Se a resposta for uma array, atualize de acordo
        setRespostas(prevRespostas => {
            const currentResposta = prevRespostas[perguntaId] || [];
            return {
                ...prevRespostas,
                [perguntaId]: checked
                    ? [...currentResposta, value]  // Adiciona a opção marcada
                    : currentResposta.filter(item => item !== value),  // Remove a opção desmarcada
            };
        });
    } else {
        // Para inputs de texto e radio, armazena a resposta como string
        setRespostas(prevRespostas => ({
            ...prevRespostas,
            [perguntaId]: event.target.value,
        }));
    }
};


const handleSubmit = async () => {
  setLoading(true);

  try {
    // Cria um array de respostas a partir do objeto `respostas`
    const respostasArray = Object.keys(respostas).map((perguntaId) => ({
      id_pergunta: perguntaId,
      resposta: respostas[perguntaId],  // Isso pode ser string ou array, depende da pergunta
    }));

    // Envia o array para o backend
    await pesquisaService.enviarRespostas(respostasArray);

    setEnvioSucesso(true);
    setModoEdicao(false);
  } catch (error) {
    console.error('Erro ao submeter as respostas:', error);
    setEnvioSucesso(false);
  } finally {
    setLoading(false);
  }
};




	const renderSubPerguntas = (subperguntas) => {
			return subperguntas.map((sub) => (
					<Pergunta
							key={sub.id}
							pergunta={sub}
							respostas={respostas}
							handleChange={handleChange}
							renderSubPerguntas={renderSubPerguntas}
							setRespostas={setRespostas}
							modoEdicao={modoEdicao}
							classes={classes}
					/>
			));
	};

	const handleEdit = () => {
			setModoEdicao(true);
	};

	return (
			<Container className={classes.container}>
					{carregandoPerguntas ? (
							<div className={classes.loadingContainer}>
									<CircularProgress />
							</div>
					) : (
							<>
									{envioSucesso !== null && (
											<Alert severity={envioSucesso ? "success" : "error"} className={classes.alert}>
													{envioSucesso ? 'Questionário enviado com sucesso!' : 'Pesquisa não enviada, tente novamente mais tarde.'}
											</Alert>
									)}

									{jaRespondido && !modoEdicao ? (
											<>
													<Typography variant="h4" className={classes.titulo}>Respostas já enviadas</Typography>
													{perguntas.filter(pergunta => !pergunta.pai_id).map((pergunta) => (
															<Pergunta
																	key={pergunta.id}
																	pergunta={pergunta}
																	respostas={respostas}
																	handleChange={handleChange}
																	renderSubPerguntas={renderSubPerguntas}
																	setRespostas={setRespostas}
																	modoEdicao={modoEdicao}
																	classes={classes}
															/>
													))}
													<Button
															variant="contained"
															color="primary"
															onClick={handleEdit}
															disabled={loading}
													>
															{loading ? 'Editando...' : 'Editar'}
													</Button>
											</>
									) : (
											<FormularioPesquisa
													perguntas={perguntas}
													respostas={respostas}
													handleChange={handleChange}
													handleSubmit={handleSubmit}
													loading={loading}
													renderSubPerguntas={renderSubPerguntas}
													modoEdicao={modoEdicao}
													classes={classes}
													setRespostas={setRespostas}


											/>
									)}
							</>
					)}
			</Container>
	);
};

export default ResponderQuestionario;